import React, { useState, useEffect } from "react";
import { Copy, Check, Users, Plus, X } from "lucide-react";
import { toast } from "sonner";

const MAX_SLOTS = 5;
const STORAGE_KEY = "nd_spotify_test_users";

/**
 * Local-only tracker for the 5 Spotify Test-User slots.
 * We can't query Spotify's dashboard API to see who's been added there, so we
 * store the list client-side (per browser) as a lightweight visual reminder.
 * DJ types the friend's Spotify-registered email, taps "invite", and gets a
 * pre-filled mailto: link + copyable invite text they can DM/WhatsApp instead.
 */
export default function SpotifyTestUserOnboarding() {
  const [slots, setSlots] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [copiedField, setCopiedField] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setSlots(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  const persist = (next) => {
    setSlots(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };

  const addSlot = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    if (slots.length >= MAX_SLOTS) {
      toast.error(`Spotify allows max ${MAX_SLOTS} test users per app`);
      return;
    }
    if (slots.some(s => s.email === email)) {
      toast.error("Already added");
      return;
    }
    persist([...slots, { email, added_at: new Date().toISOString() }]);
    setNewEmail("");
    toast.success(`Added ${email} — now paste it in the Spotify dashboard`);
  };

  const removeSlot = (email) => {
    persist(slots.filter(s => s.email !== email));
  };

  const inviteMessage = (email = "there") => `hey — i'm building a DJ app on Spotify and it's in dev mode, so i can only pull playlists for people i've added as test users.

can i add your Spotify account (${email !== "there" ? email : "the email you use to log in to Spotify"}) so you can browse your library through my mixer? nothing else changes on your end — you'll just get a "Developer Mode" banner once when you first log in.

cheers`;

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 1500);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — long-press to select");
    }
  };

  const slotsFree = MAX_SLOTS - slots.length;

  return (
    <div className="mt-2 pt-3 border-t border-[#FFC107]/20" data-testid="spotify-onboarding-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[#F8FAFC] text-[11px] font-mono uppercase tracking-widest">
          <Users className="w-3.5 h-3.5 text-[#1DB954]"/>
          Test-user slots
        </div>
        <div className="text-[10px] font-mono" data-testid="spotify-slots-counter">
          <span className={slotsFree === 0 ? "text-[#FF2A6D]" : "text-[#1DB954]"}>{slots.length}</span>
          <span className="text-[#94A3B8]"> / {MAX_SLOTS} used</span>
        </div>
      </div>

      {/* Slot grid */}
      <div className="grid grid-cols-1 gap-1 mb-2" data-testid="spotify-slot-list">
        {slots.map((s) => (
          <div key={s.email} data-testid={`spotify-slot-${s.email}`}
               className="group flex items-center gap-2 px-2 py-1.5 rounded bg-[#0B0E14]/60 border border-[#1DB954]/20 text-[10.5px]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1DB954]"/>
            <span className="flex-1 text-[#F8FAFC] font-mono truncate">{s.email}</span>
            <button type="button" data-testid={`spotify-slot-copy-${s.email}`}
                    onClick={() => copy(s.email, `slot-${s.email}`)}
                    className="text-[#94A3B8] hover:text-white transition p-0.5" title="Copy email">
              {copiedField === `slot-${s.email}` ? <Check className="w-3 h-3 text-[#1DB954]"/> : <Copy className="w-3 h-3"/>}
            </button>
            <button type="button" data-testid={`spotify-slot-invite-${s.email}`}
                    onClick={() => copy(inviteMessage(s.email), `msg-${s.email}`)}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#1DB954] hover:text-[#1ed760] transition px-1.5"
                    title="Copy invite message">
              {copiedField === `msg-${s.email}` ? "Copied" : "Invite"}
            </button>
            <button type="button" data-testid={`spotify-slot-remove-${s.email}`}
                    onClick={() => removeSlot(s.email)}
                    className="text-[#94A3B8] hover:text-[#FF2A6D] transition p-0.5 opacity-0 group-hover:opacity-100"
                    title="Remove">
              <X className="w-3 h-3"/>
            </button>
          </div>
        ))}
        {slots.length === 0 && (
          <div className="text-[10.5px] text-[#64748B] italic px-1 py-1" data-testid="spotify-slots-empty">
            No test users yet. Add a friend's Spotify email below.
          </div>
        )}
      </div>

      {/* Add-slot form */}
      {slots.length < MAX_SLOTS && (
        <form onSubmit={(e) => { e.preventDefault(); addSlot(); }}
              data-testid="spotify-slot-add-form"
              className="flex gap-1.5">
          <input type="email"
                 data-testid="spotify-slot-email-input"
                 value={newEmail}
                 onChange={(e) => setNewEmail(e.target.value)}
                 placeholder="friend@spotify.com"
                 className="flex-1 px-2 py-1.5 rounded bg-[#0B0E14]/60 border border-[#1DB954]/30 text-[11px] font-mono text-[#F8FAFC] placeholder-[#64748B]/60 focus:outline-none focus:border-[#1DB954] focus:ring-1 focus:ring-[#1DB954]/40 transition"/>
          <button type="submit"
                  data-testid="spotify-slot-add-button"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-[#1DB954] text-black hover:bg-[#1ed760] font-black uppercase tracking-widest text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={!newEmail.trim().includes("@")}>
            <Plus className="w-3 h-3"/> Track
          </button>
        </form>
      )}
      {slots.length >= MAX_SLOTS && (
        <div className="text-[10px] text-[#FF2A6D] font-mono uppercase tracking-widest" data-testid="spotify-slots-full">
          Slots full — remove one or apply for Extended Quota
        </div>
      )}

      {/* Copyable invite template (blank / generic) */}
      <details className="mt-2 group" data-testid="spotify-invite-template">
        <summary className="cursor-pointer text-[10.5px] text-[#94A3B8] hover:text-white font-mono uppercase tracking-widest select-none list-none flex items-center gap-1">
          <span className="text-[#1DB954] transition group-open:rotate-90 inline-block">▸</span>
          Copy invite message template
        </summary>
        <div className="mt-2 relative">
          <pre className="text-[10.5px] text-[#F8FAFC]/85 whitespace-pre-wrap bg-[#0B0E14]/60 border border-[#1DB954]/20 rounded p-2 pr-8 font-mono leading-relaxed" data-testid="spotify-invite-template-text">
{inviteMessage()}
          </pre>
          <button type="button"
                  data-testid="spotify-invite-template-copy"
                  onClick={() => copy(inviteMessage(), "template")}
                  className="absolute top-2 right-2 text-[#94A3B8] hover:text-white p-1 transition"
                  title="Copy to clipboard">
            {copiedField === "template" ? <Check className="w-3.5 h-3.5 text-[#1DB954]"/> : <Copy className="w-3.5 h-3.5"/>}
          </button>
        </div>
      </details>
    </div>
  );
}
